import React, { useEffect, useState } from 'react';

import { useLocalization } from '@progress/kendo-react-intl';
import { useHistory, useLocation } from "react-router-dom";
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Error } from "@progress/kendo-react-labels";
import { requiredValidator, partsCodeValidator, emailOrPartsCodeValidator } from '../validators';

import { AppContext } from './../AppContext'
import { useStores } from 'src/stores';
import { useServices } from 'src/services';
import { Input } from 'src/components/form/Input';
import { MaskedTextBox } from 'src/components/form/MaskedTextBox';
// @ts-ignore
import PrivacyBanner from 'react-ford-ccpa-banner';
import logo from "../assets/images/Ford_BuildingTogether_Blue_Screen_RGB_Vt_v2.png"
import sideImage from "../assets/images/2025_D4URC_Dealer_Portal_Login_Page_66800.png";
import '../styles/_login.scss';

const LoginScreen = () => {
    const { ...formValues } = React.useContext(AppContext);
    const localizationService = useLocalization();
    const { language } = localizationService;
    const history = useHistory();

    const [loginError, setLoginError] = useState<string>('');
    const [salesCodeEntered, setSalesCodeEntered] = useState<boolean>(false);
    const [salesCodeError, setSalesCodeError] = useState<string>('');
    const [partsCode, setPartsCode] = useState<string>('');
    const [showAccountExistsMessage, setShowAccountExistsMessage] = useState<boolean>(false);
    const { login } = useStores();
    const { d4ur } = useServices();

    let location = useLocation<Location>();

    let { from, skipSalesCode, partsCode: routePartsCode, fromSignUp, redirectTo } = location.state as any || { from: { pathname: "/events" } };

    const validateAndSkipSalesCode = React.useCallback(async (partsCode: string) => {
        try {
            const response: any = await d4ur.getDealerInfo(partsCode, true);
            if (response.result && response.result.dealer_info) {
                const dealerExists = response.result.dealer_exists === true;
                if (dealerExists) {
                    setPartsCode(partsCode);
                    setSalesCodeEntered(true);
                } else {
                    // Account no longer exists, clear stored code
                    localStorage.removeItem('d4ur_last_validated_parts_code');
                }
            } else {
                // Sales code is no longer valid, clear stored code
                localStorage.removeItem('d4ur_last_validated_parts_code');
            }
        } catch (error) {
            localStorage.removeItem('d4ur_last_validated_parts_code');
        }
    }, [d4ur]);

    useEffect(() => {
        if (login.dealer) {
            console.log('[LoginScreen]', 'I think we are already logged in');
            d4ur.getAccount().then(() => {
                history.push('/events');
            });
            return;
        }
        
        // If coming from "Create an Account", clear any stored sales code to force fresh entry
        if (fromSignUp) {
            localStorage.removeItem('d4ur_last_validated_parts_code');
            // Stay on sales code entry (default state)
            return;
        }
        
        // If skipSalesCode is true, go directly to email/password form
        if (skipSalesCode) {
            setSalesCodeEntered(true);
        } else {
            // Check localStorage for stored sales code
            const storedPartsCode = localStorage.getItem('d4ur_last_validated_parts_code');
            if (storedPartsCode) {
                validateAndSkipSalesCode(storedPartsCode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSalesCodeSubmit = React.useCallback(
        async (dataItem) => {
            const partsCode = dataItem.sales_code;
            setSalesCodeError('');
            
            // Check if the parts code is valid using the validator
            const validationResult = partsCodeValidator(partsCode);
            
            if (validationResult) {
                setSalesCodeError('Invalid sales code');
                console.log('[LoginScreen]', 'Sales Code validation failed:', validationResult);
                return;
            }

            try {
                const response: any = await d4ur.getDealerInfo(partsCode, true);
                console.log('[LoginScreen]', 'Dealer info response:', response);

                if (response.result && response.result.dealer_info) {
                    // Check if dealer already exists using the new dealer_exists flag
                    const dealerExists = response.result.dealer_exists === true;
                    
                    // If there's a redirectTo flag, redirect to that path after validating sales code
                    if (redirectTo) {
                        console.log('[LoginScreen]', 'SUCCESS: Valid sales code - Redirecting to', redirectTo);
                        localStorage.setItem('d4ur_last_validated_parts_code', partsCode);
                        history.push(redirectTo, { partsCode });
                        return;
                    }
                    
                    if (dealerExists) {
                        // State 2: Valid code, but account already exists - go to login form
                        console.log('[LoginScreen]', 'SUCCESS: Valid sales code - A Dealer account already exists for this sales code. Please login or reset your password.');
                        setPartsCode(partsCode); // Store parts code for forgot username
                        setSalesCodeEntered(true); // Show login form
                        
                        // Show message only if coming from "Create an Account" button
                        if (fromSignUp) {
                            setShowAccountExistsMessage(true);
                        } else {
                            localStorage.setItem('d4ur_last_validated_parts_code', partsCode);
                        }
                    } else {
                        // State 1: Valid code, not in use - redirect to signup
                        console.log('[LoginScreen]', 'SUCCESS: Valid sales code');
                        history.push('/signup', { partsCode });
                    }
                } else {
                    // State 3: Invalid dealer code
                    setSalesCodeError('Invalid sales code');
                    console.log('[LoginScreen]', 'FAILURE: Invalid sales code - No dealer information found for this sales code');
                }
            } catch (error) {
                setSalesCodeError('Invalid sales code');
                console.error('[LoginScreen]', 'FAILURE: Error fetching dealer info:', error);
            }
        },
        [d4ur, history, fromSignUp, redirectTo]
    );

    const onSubmit = React.useCallback(
        async (dataItem) => {
            login.setLoginEmail(dataItem.email);
            const call: any = await d4ur.doLogin(dataItem.email, dataItem.password);
            if (call.result.ERROR) {
                console.log('[LoginScreen]', 'Login Error');
                return setLoginError(call.result.message);
            }
            if (call.result.dealer) {
                console.log('[LoginScreen]', 'logged in, no 2fa required');
                login.setDealerToken(call.result.token)
                login.setDealer(call.result.dealer as Dealer);
                
                // Check if this is the dealer's first login this season
                const dealerId = call.result.dealer.dealer_id;
                const firstLoginKey = `d4ur_first_login_${dealerId}`;
                const hasLoggedInBefore = localStorage.getItem(firstLoginKey);
                
                if (!hasLoggedInBefore) {
                    // Mark as logged in for future sessions
                    localStorage.setItem(firstLoginKey, 'true');
                    // Redirect to profile with first login flag
                    history.push('/profile', { isFirstLogin: true });
                } else {
                history.push(from);
                }
            }
        },
        [login, from, history, d4ur]
    );

    return (
        <div id="Login" className="login_page">
            <div className='main'>
                <div className="left_content">
                    {/* <div className={'logo_container'}> */}
                        <img src={logo} alt={'Ford Drive 4 UR School Logo'} className='logo' onClick={() => {
                                        history.push("/");
                                    }} />
                    {/* </div> */}
                    <div className="form-container" style={{ maxWidth: 700 }}>
                        {!salesCodeEntered ? (
                            <>
                            <h1 className="form_title">Log In</h1>
                                <Form
                                    onSubmit={onSalesCodeSubmit}
                                    render={(formRenderProps) => (
                                        <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                                            {salesCodeError && <Error>{salesCodeError}</Error>}
                                            <Field
                                                id={'sales_code'}
                                                name={'sales_code'}
                                                label={`${localizationService.toLanguageString(
                                                    "custom.sales_code",
                                                    "Sales Code"
                                                )}*`}
                                                validator={partsCodeValidator}
                                                hint={
                                                    "The Dealer Sales Code starts with an F-. Please enter the 5 digits that follow the F."
                                                }
                                                required={true}
                                                mask="00000"
                                                component={MaskedTextBox}
                                            />
                                            <div className='log_in_button'>
                                                <Button
                                                    className={'log_in_btn'}
                                                    primary={true}
                                                    type={'submit'}
                                                    disabled={!formRenderProps.allowSubmit}
                                                >
                                                    Continue
                                                </Button>
                                            </div>
                                        </FormElement>
                                    )}
                                />
                            </>
                        ) : (
                            <>
                                <h1 className="form_title">Log In</h1>
                                {showAccountExistsMessage && (
                                    <p style={{ 
                                        backgroundColor: '#fff3cd', 
                                        border: '1px solid #ffc107', 
                                        borderRadius: '4px', 
                                        padding: '12px 16px',
                                        marginBottom: '20px',
                                        fontSize: '14px',
                                        color: '#856404'
                                    }}>
                                        There is already an account associated with this Sales Code, please Log In.
                                    </p>
                                )}
                            <Form
                                onSubmit={onSubmit}
                                initialValues={{
                                    ...formValues,
                                    email: login.loginEmail,
                                }}
                                render={(formRenderProps) => (
                                    <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                                        {loginError && <Error>{loginError}</Error>}
                                        <Field
                                            id={'email'}
                                            name={'email'}
                                                type={'text'}
                                                placeholder={'e.g.: peter@gmail.com or 12345'}
                                                label={localizationService.toLanguageString('custom.loginEmail', 'Email Address or Sales Code')}
                                                validator={emailOrPartsCodeValidator}
                                            required={true}
                                            component={Input}
                                        />
                                        <Field
                                            id={'password'}
                                            name={'password'}
                                            type={'password'}
                                            label={localizationService.toLanguageString('custom.password', 'Password')}
                                            validator={requiredValidator}
                                            component={Input}
                                        />
                                        <div className='log_in_button'>
                                            <Button
                                                className={'log_in_btn'}
                                                primary={true}
                                                type={'submit'}
                                                disabled={!formRenderProps.allowSubmit}
                                            >
                                                {localizationService.toLanguageString('custom.logIn', 'Log In')}
                                            </Button>
                                        </div>

                                    </FormElement>
                                )}
                            />
                            <div className="sign_up_container">
                                {/* TODO: ENABLE THIS BEFORE APRIL 28TH LAUNCH */}
                                {/* <div className='forgot_pass_container'>
                                <p className='sign_up_text'>Don't have an account?</p>
                                <button 
                                        className='sign_up_btn' 
                                        onClick={() => {
                                            history.push("/signup");
                                        }}>
                                        Sign Up
                                    </button>
                                </div> */}
                                    <div className='forgot_pass_container'>
                                        <button className='forgot_pass_btn' onClick={() => {
                                    // Use partsCode from state if available, otherwise from route state
                                    const partsCodeToUse = partsCode || routePartsCode;
                                    history.push("/forgot-username", { partsCode: partsCodeToUse });
                                }}>
                                            Forgot Username?
                                        </button>
                                    </div>
                                <div className='forgot_pass_container'>
                                    <button className='forgot_pass_btn' onClick={() => {
                                history.push("/password");
                            }}>
                                        Forgot Password?
                                    </button>
                                </div>
                            </div>
                            </>
                        )}
                    </div>
                </div>
                <div className='right_content'>
                    <img src={sideImage} alt={'Two volunteers driving a Ford vehicle.'} className='right_img' />
                </div>

            </div>
            <PrivacyBanner
                containerStyle={{ padding: '10px 0 0', width: '100%' }}
                privacyContainerStyle={{ padding: '10px 0', width: "60%", marginLeft: '20%' }}
                showPrivacy={true}
                language={language}
                team={'ford'}
                imgStyle={{ paddingLeft: '30px' }}
                otherLinks={[
                    { text: "CONTACT US", url: "/contact", target: '_blank' },
                    { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: '_blank'},
                    { text: "PROGRAM RESOURCES ", url: "/resources", target: '_blank' },
                ]}
            />
        </div>
    );
}

export default LoginScreen;
